import React from 'react'

import Axios from '../../../networking/intraceptor'
import { update_frequency_qty } from '../../../networking/urlEndPoints'
import { showToast } from '../../../utils/helper/helper'

/**
 * Minimum time that must pass between two consecutive doses of this RX.
 *
 * Prefilled from the SIG when the prescriber stated one ("MUST BE ATLEAST 3
 * HOURS APART"), otherwise the pharmacist sets it by hand. The value gates the
 * Driver and In-Store apps: delivering a dose early there raises a confirmation
 * and records the reason in the audit trail.
 *
 * Shown only where the backend says the rule applies (safe-supply DILAUDID),
 * and never for OD - a once-daily RX has no second dose to space out.
 */

// Matches the range the backend accepts (SigDoseGapExtractor 30-720 minutes).
export const MIN_GAP_MINUTES = 30
export const MAX_GAP_MINUTES = 720

export const humanizeDoseGap = (minutes) => {
    const total = Number(minutes)
    if (!Number.isFinite(total) || total <= 0) return ''

    const hours = Math.floor(total / 60)
    const remainder = total % 60
    if (hours === 0) return `${remainder} minutes`

    const hourLabel = `${hours} ${hours === 1 ? 'hour' : 'hours'}`
    return remainder === 0 ? hourLabel : `${hourLabel} ${remainder} minutes`
}

/**
 * The field is typed in hours because that is how every SIG states it, while
 * the backend stores and enforces minutes. 1.5 is kept as 90 rather than
 * rounded, since "MINIMUM 90 MINUTES" is real wording in the SIG set.
 */
export const hoursToMinutes = (hours) => Math.round(Number(hours) * 60)

export const minutesToHoursInput = (minutes) => {
    const total = Number(minutes)
    if (!Number.isFinite(total) || total <= 0) return ''

    // Trim 2 -> "2" but keep 1.5 -> "1.5".
    return String(Number((total / 60).toFixed(2)))
}

/** The gap saved on the rows, if any. Cycles share one value in practice. */
export const getDoseGapFromRows = (frequencyRows = []) => {
    const row = (frequencyRows || []).find(item => (
        item?.frequency !== 'OD'
        && item?.delivery_gap_minutes != null
        && item?.delivery_gap_minutes !== ''
    ))

    return row ? Number(row.delivery_gap_minutes) : null
}

/** Whether a gap applies at all: any non-OD cycle makes it relevant. */
export const hasNonOdFrequencyRow = (frequencyRows = []) => (
    (frequencyRows || []).some(row => row?.frequency && row.frequency !== 'OD')
)

const DoseGapField = ({
    frequencyRows,
    setFrequencyRows,
    deliveryGap,
    rxesId,
    disabled = false
}) => {
    // What the user is typing, so a half-finished "1." is not fought by the
    // parsed value. Null means the input is showing the saved value. Declared
    // before any early return, or the hook order changes between renders.
    const [draft, setDraft] = React.useState(null)

    // What the server last stored. handleChange writes every keystroke into
    // frequencyRows so the rest of the panel keeps up, which means the rows
    // cannot answer "has this changed since it was saved" - by the time the
    // field is left they already hold what was typed. Declared with the state
    // above, before any early return, to keep the hook order stable.
    const lastSavedRef = React.useRef(undefined)

    // Whether the rule applies at all is the backend's call - it scopes dose
    // separation to safe-supply DILAUDID - so the panel does not repeat that
    // test here and cannot drift from what delivery actually enforces.
    if (!deliveryGap?.is_applicable) return null
    if (!hasNonOdFrequencyRow(frequencyRows)) return null

    const savedMinutes = getDoseGapFromRows(frequencyRows)

    if (lastSavedRef.current === undefined) {
        lastSavedRef.current = savedMinutes
    }
    const sigMinutes = deliveryGap?.sig_minutes != null ? Number(deliveryGap.sig_minutes) : null

    // The rows are the source of truth once anything is saved; the describe
    // block only seeds a prescription nobody has set a gap on yet.
    const currentMinutes = savedMinutes != null
        ? savedMinutes
        : (deliveryGap?.minutes != null ? Number(deliveryGap.minutes) : null)

    const isMissing = currentMinutes == null

    // What the SIG states, when it disagrees with the value in the box. Null
    // when they agree, when the SIG states nothing, or when nothing is set -
    // in all of those there is nothing to warn about.
    const mismatchMinutes = currentMinutes != null && sigMinutes != null && currentMinutes !== sigMinutes
        ? sigMinutes
        : null

    const inputValue = draft != null ? draft : minutesToHoursInput(currentMinutes)

    const typedHours = inputValue.trim() === '' ? null : Number(inputValue)
    const typedMinutes = typedHours != null && Number.isFinite(typedHours)
        ? hoursToMinutes(typedHours)
        : null

    const error = (() => {
        if (inputValue.trim() === '') return null
        if (typedMinutes == null) return 'Enter a number of hours, for example 3 or 1.5.'
        if (typedMinutes < MIN_GAP_MINUTES) return `Must be at least ${humanizeDoseGap(MIN_GAP_MINUTES)}.`
        if (typedMinutes > MAX_GAP_MINUTES) return `Cannot be more than ${humanizeDoseGap(MAX_GAP_MINUTES)}.`
        return null
    })()

    const writeToRows = (minutes) => {
        setFrequencyRows((rows) => (rows || []).map(row => (
            row?.frequency === 'OD'
                ? { ...row, delivery_gap_minutes: '', delivery_gap_source: null }
                : {
                    ...row,
                    delivery_gap_minutes: minutes ?? '',
                    // Accepting exactly what the SIG stated stays "sig"; anything
                    // else is the pharmacist's own call and must survive a
                    // later SIG re-parse.
                    delivery_gap_source: minutes == null ? null : (minutes === sigMinutes ? 'sig' : 'manual')
                }
        )))
    }

    const handleChange = (event) => {
        const raw = event.target.value

        // Digits and a single decimal point only, so the field cannot hold
        // something the backend would reject.
        if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return

        setDraft(raw)

        if (raw.trim() === '') {
            writeToRows(null)
            return
        }

        const minutes = hoursToMinutes(raw)
        if (Number.isFinite(minutes) && minutes >= MIN_GAP_MINUTES && minutes <= MAX_GAP_MINUTES) {
            writeToRows(minutes)
        }
    }

    // Hand the input back to the saved value once the user is done with it, and
    // save the gap on its own.
    //
    // The UPDATE button below sends the whole frequency payload and stays
    // disabled while the dose rows fail the quantity check, which left the gap
    // unsavable even though it is unrelated to the doses. This posts to that
    // same endpoint with gap_only, which stores delivery_gap_minutes and
    // nothing else, so the doses on screen are never sent and the existing
    // button keeps behaving exactly as before.
    const handleBlur = () => {
        const typed = inputValue.trim()
        setDraft(null)

        if (error || !rxesId) return

        const minutes = typed === '' ? null : typedMinutes
        if (minutes === lastSavedRef.current) return

        Axios.post(update_frequency_qty, {
            gap_only: true,
            rxes_id: rxesId,
            delivery_gap_minutes: minutes,
        }).then((response) => {
            lastSavedRef.current = minutes
            const warning = response?.data?.data?.dose_gap_warning
            showToast(warning || 'Delivery gap saved.', warning ? 'warning' : 'success')
        }).catch((err) => {
            // Put the rows back to the stored value, so the box cannot show a
            // number that was never saved.
            writeToRows(lastSavedRef.current)
            showToast(
                err?.response?.data?.message || 'Could not save the delivery gap. Please try again.',
                'error'
            )
        })
    }

    return (
        // A fragment, not a wrapper: this renders inside the dose row's flex
        // container, so each part has to be a sibling column of the dose inputs
        // rather than a nested block that would break the row's alignment.
        <>
                {/* Sized and styled like the dose inputs beside it, with the
                    unit as a fixed suffix the way IV code sits on a dose. */}
                <div className="flex flex-col w-[110px] min-w-[110px] uppercase">
                    <label htmlFor="delivery_gap_minutes" className="text-gray-500 text-[11px]">
                        Delivery Gap
                    </label>
                    <div className={`flex overflow-hidden border rounded-md h-[42px]
                        ${error ? 'border-red-500' : isMissing ? 'border-amber-400' : 'border-gray-300'}`}>
                        <input
                            id="delivery_gap_minutes"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="--"
                            value={inputValue}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="flex-1 min-w-0 w-full px-2 py-1 text-gray-700 bg-white border-none
                                focus:ring-2 focus:ring-transparent focus:outline-none
                                disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                        />
                        <span className="h-[42px] w-[40px] min-w-[40px] px-1 text-[10px] border-l border-l-gray-200
                            bg-gray-50 text-gray-500 text-center flex items-center justify-center select-none">
                            HRS
                        </span>
                    </div>
                </div>

                {/* Silent when the value agrees with the SIG, which is the
                    normal case - the number in the box says it already. Only a
                    problem earns space in the row: a value the backend would
                    reject, or one that contradicts the prescriber. */}
                {(error || mismatchMinutes !== null) && (
                    <div className="flex flex-col justify-end pb-[4px] max-w-[230px] normal-case leading-tight">
                        {error ? (
                            <span className="text-[11px] text-red-600">{error}</span>
                        ) : (
                            <span className="text-[11px] text-amber-600">
                                SIG states {humanizeDoseGap(mismatchMinutes)}.
                            </span>
                        )}
                    </div>
                )}
        </>
    )
}

export default DoseGapField
